import React, { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import PropTypes from "prop-types";
import { RoomSettingsSidebar } from "./FluentRoomSettingsSidebar";
import { AudioVideoSettings } from "../room/AudioVideoSettings";
import { PreferredMicSelect } from "./PreferredMicSelect";
import { PreferredSpeakerSelect } from "./PreferredSpeakerSelect";
import {
  AudioClippingThresholdSlider,
  CursorSizeSlider,
  MediaVolumeSlider,
  MovementSpeedSlider,
  NametagVisibilityDistanceSlider,
  SnapRotationDegreesSlider,
  SpecialEffectsVolumeSlider,
  VoiceVolumeSlider
} from "../room/RoomSettingsSidebarSliders";
import { makeStyles } from "@fluentui/react-components";
import {
  AllowMultipleHubsInstancesSwitch,
  AnimateWaypointTransitionsSwitch,
  DisableAudioPanningSwitch,
  DisableAutoPixelRatioSwitch,
  DisableBackwardsMovementSwitch,
  DisableIdleDetectionSwitch,
  DisableMicAutoGainControlSwitch,
  DisableMicEchoCancellationSwitch,
  DisableMicNoiseSuppressionSwitch,
  DisableMovementSwitch,
  DisableSoundEffectsSwitch,
  DisableStrafingSwitch,
  DisableTeleporterSwitch,
  EnableAudioClippingSwitch,
  EnableBloomSwitch,
  EnableDynamicShadowsSwitch,
  EnableGyroSwitch,
  EnableOnScreenJoystickLeftSwitch,
  EnableOnScreenJoystickRightSwitch,
  EnablePostEffectsSwitch,
  InvertTouchscreenCameraMoveSwitch,
  LazyLoadSceneMediaSwitch,
  PreferMobileObjectInfoPanelSwitch,
  ShowAudioDebugPanelSwitch,
  ShowFPSCounterSwitch
} from "../room/RoomSettingsSidebarSwitches";
import {
  AntiAliasingModeSelect,
  LocaleSelect,
  MaterialQualitySelect,
  NametagVisibilitySelect,
  PanningQualitySelect,
  ThemeSelect
} from "../room/RoomSettingsSidebarSelects";

import { useMicrophone } from "../room/useMicrophone";
import { useMicrophoneStatus } from "../room/useMicrophoneStatus";
import { useSpeakers } from "../room/useSpeakers";
import { useCameraDevices } from "../room/useCameraDevices";
import { PreferredVideoSelect } from "../room/PreferredVideoSelect";
import configManager from "../../singleton/ConfigManager";
import { GraphicsSettings } from "../room/GraphicsSettings";
import MaxResolutionSlider from "../room/MaxResolutionSlider";
import { ControlsSettings } from "../room/ControlsSettings";
import { GeneralSettings } from "../room/GeneralSettings";
import notificationsCenter from "../../singleton/NotificationsCenter";
import { useIntl } from "react-intl";
import { VerseSettingsContainer } from "../room/VerseSettingsContainer";
import { useSettingsContext } from "../room/SettingsContext";

const useStyles = makeStyles({
  row: {
    display: "flex",
    minHeight: "var(--lineHeightBase600, 32px)",
    gap: "10px",
    justifyContent: "space-between",
    alignItems: "center",
    "& > *": {
      "@media (min-width: 768px)": {
        maxWidth: "50%"
      }
    },
    "& .fui-Select": {
      "@media (min-width: 768px)": {
        minWidth: "200px"
      }
    }
  },
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    padding: "20px",
    width: "100%"
  }
});

const NotifiablePermissions = ["text_chat", "voice_chat"];

export function RoomSettingsSidebarContainer({ store, scene, hubChannel, hub, dashboardChannel }) {
  const styles = useStyles();

  const { open, onOpenChange, selectedTab, onTabChange, showVerseSettings } = useSettingsContext();
  const { isMicEnabled } = useMicrophoneStatus(scene);
  const { micDeviceChanged, micDevices } = useMicrophone(scene);
  const { videoDeviceChanged, videoDevices, fetchVideoDevices, getVideoDevices } = useCameraDevices(scene);

  useEffect(() => {
    if (configManager.config.spawnCamera?.enabled && open == true) {
      getVideoDevices();
    }
  }, [getVideoDevices, open]);

  const handleFetchVideoDevices = useCallback(() => {
    if (configManager.config.spawnCamera?.enabled) {
      fetchVideoDevices();
    }
  }, [fetchVideoDevices]);

  const [, forceUpdate] = useReducer(x => !x, false);

  useEffect(() => {
    const storeUpdated = () => {
      forceUpdate(n => !n);
    };

    store.addEventListener("statechanged", storeUpdated);
    window.addEventListener("locale-updated", storeUpdated);

    return () => {
      store.removeEventListener("statechanged", storeUpdated);
      window.removeEventListener("locale-updated", storeUpdated);
    };
  }, [store]);

  const handleTabSelect = useCallback(
    (_, data) => {
      onTabChange(data.value);
    },
    [onTabChange]
  );

  const { speakerDeviceChanged, speakerDevices } = useSpeakers();

  const voiceVolume = store.state.preferences.globalVoiceVolume;
  const onVoiceVolumeChange = useCallback(
    (_, data) => {
      store.update({ preferences: { globalVoiceVolume: data.value } });
    },
    [store]
  );

  const mediaVolume = store.state.preferences.globalMediaVolume;
  const onMediaVolumeChange = useCallback(
    (_, data) => {
      store.update({ preferences: { globalMediaVolume: data.value } });
    },
    [store]
  );

  const specialEffectsVolume = store.state.preferences.globalSFXVolume;
  const onSpecialEffectsVolumeChange = useCallback(
    (_, data) => {
      store.update({ preferences: { globalSFXVolume: data.value } });
    },
    [store]
  );

  const audioClippingThreshold = store.state.preferences.audioClippingThreshold;
  const onAudioClippingThresholdChange = useCallback(
    // We setup a setter in the AudioClippingThresholdSlider component that pass the value to this callback
    value => {
      store.update({ preferences: { audioClippingThreshold: value } });
    },
    [store]
  );

  const preferenceSliderHandlers = {
    snapRotationDegrees: value => store.update({ preferences: { snapRotationDegrees: value } }),
    movementSpeedModifier: value => store.update({ preferences: { movementSpeedModifier: value } }),
    cursorSize: value => store.update({ preferences: { cursorSize: value } }),
    nametagVisibilityDistance: value => store.update({ preferences: { nametagVisibilityDistance: value } })
  };

  const useSliderPreference = preferenceKey => {
    const preferenceValue = store.state.preferences[preferenceKey];
    const onChange = useCallback(
      (_, data) => {
        preferenceSliderHandlers[preferenceKey](data.value);
      },
      [preferenceKey]
    );
    return [preferenceValue, onChange];
  };

  const [snapRotationDegrees, onSnapRotationDegreesChange] = useSliderPreference("snapRotationDegrees");
  const [movementSpeedModifier, onMovementSpeedModifierChange] = useSliderPreference("movementSpeedModifier");
  const [cursorSize, onCursorSizeChange] = useSliderPreference("cursorSize");
  const [nametagVisibilityDistance, onNametagVisibilityDistanceChange] =
    useSliderPreference("nametagVisibilityDistance");

  const preferenceToggleHandlers = {
    disableSoundEffects: value => store.update({ preferences: { disableSoundEffects: value } }),
    disableEchoCancellation: value =>
      store.update({ preferences: { disableEchoCancellation: value, shouldPromptForRefresh: true } }),
    disableNoiseSuppression: value =>
      store.update({ preferences: { disableNoiseSuppression: value, shouldPromptForRefresh: true } }),
    disableAutoGainControl: value =>
      store.update({ preferences: { disableAutoGainControl: value, shouldPromptForRefresh: true } }),
    enableAudioClipping: value => store.update({ preferences: { enableAudioClipping: value } }),
    showAudioDebugPanel: value => store.update({ preferences: { showAudioDebugPanel: value } }),
    disableLeftRightPanning: value => store.update({ preferences: { disableLeftRightPanning: value } }),
    enableDynamicShadows: value => store.update({ preferences: { enableDynamicShadows: value } }),
    disableAutoPixelRatio: value => store.update({ preferences: { disableAutoPixelRatio: value } }),
    enableBloom: value => store.update({ preferences: { enableBloom: value, shouldPromptForRefresh: true } }),
    enablePostEffects: value =>
      store.update({ preferences: { enablePostEffects: value, shouldPromptForRefresh: true } }),
    disableMovement: value => store.update({ preferences: { disableMovement: value } }),
    disableBackwardsMovement: value => store.update({ preferences: { disableBackwardsMovement: value } }),
    disableStrafing: value => store.update({ preferences: { disableStrafing: value } }),
    disableTeleporter: value => store.update({ preferences: { disableTeleporter: value } }),
    enableOnScreenJoystickLeft: value => store.update({ preferences: { enableOnScreenJoystickLeft: value } }),
    enableOnScreenJoystickRight: value => store.update({ preferences: { enableOnScreenJoystickRight: value } }),
    enableGyro: value => store.update({ preferences: { enableGyro: value } }),
    invertTouchscreenCameraMove: value => store.update({ preferences: { invertTouchscreenCameraMove: value } }),
    animateWaypointTransitions: value => store.update({ preferences: { animateWaypointTransitions: value } }),
    showFPSCounter: value => store.update({ preferences: { showFPSCounter: value } }),
    allowMultipleHubsInstances: value => store.update({ preferences: { allowMultipleHubsInstances: value } }),
    disableIdleDetection: value => store.update({ preferences: { disableIdleDetection: value } }),
    lazyLoadSceneMedia: value => store.update({ preferences: { lazyLoadSceneMedia: value } }),
    preferMobileObjectInfoPanel: value => store.update({ preferences: { preferMobileObjectInfoPanel: value } }),
    showRtcDebugPanel: value => store.update({ preferences: { showRtcDebugPanel: value } })
  };

  const useTogglePreference = preferenceKey => {
    const preferenceValue = store.state.preferences[preferenceKey];
    const onToggle = useCallback(
      (_, data) => {
        preferenceToggleHandlers[preferenceKey](data.checked);
      },
      [preferenceKey]
    );
    return [preferenceValue, onToggle];
  };

  const [isSoundEffectsDisabled, toggleSoundEffectsDisabled] = useTogglePreference("disableSoundEffects");
  const [isMicEchoCancellationDisabled, toggleMicEchoCancellationDisabled] =
    useTogglePreference("disableEchoCancellation");
  const [isMicNoiseSuppressionDisabled, toggleMicNoiseSuppressionDisabled] =
    useTogglePreference("disableNoiseSuppression");
  const [isMicAutoGainControlDisabled, toggleMicAutoGainControlDisabled] =
    useTogglePreference("disableAutoGainControl");
  const [isAudioClippingEnabled, toggleAudioClippingEnabled] = useTogglePreference("enableAudioClipping");
  const [isAudioDebugPanelShown, toggleAudioDebugPanelShown] = useTogglePreference("showAudioDebugPanel");
  const [isAudioPanningDisabled, toggleAudioPanningDisabled] = useTogglePreference("disableLeftRightPanning");
  const [isDynamicShadowsEnabled, toggleDynamicShadowsEnabled] = useTogglePreference("enableDynamicShadows");
  const [isAutoPixelRatioDisabled, toggleAutoPixelRatioDisabled] = useTogglePreference("disableAutoPixelRatio");
  const [isBloomEnabled, toggleBloomEnabled] = useTogglePreference("enableBloom");
  const [isPostEffectsEnabled, togglePostEffects] = useTogglePreference("enablePostEffects");
  const [isMovementDisabled, toggleMovementDisabled] = useTogglePreference("disableMovement");
  const [isBackwardsMovementDisabled, toggleBackwardsMovementDisabled] =
    useTogglePreference("disableBackwardsMovement");
  const [isStrafingDisabled, toggleStrafingDisabled] = useTogglePreference("disableStrafing");
  const [isTeleporterDisabled, toggleTeleporterDisabled] = useTogglePreference("disableTeleporter");
  const [isOnScreenJoystickLeftEnabled, toggleOnScreenJoystickLeftEnabled] =
    useTogglePreference("enableOnScreenJoystickLeft");
  const [isOnScreenJoystickRightEnabled, toggleOnScreenJoystickRightEnabled] =
    useTogglePreference("enableOnScreenJoystickRight");
  const [isGyroEnabled, toggleGyroEnabled] = useTogglePreference("enableGyro");
  const [isTouchscreenCameraMoveInverted, toggleTouchscreenCameraMoveInverted] =
    useTogglePreference("invertTouchscreenCameraMove");
  const [isAnimateWaypointTransitionsEnabled, toggleAnimateWaypointTransitionsEnabled] =
    useTogglePreference("animateWaypointTransitions");
  const [isShowFPSCounterEnabled, toggleShowFPSCounterEnabled] = useTogglePreference("showFPSCounter");
  const [isAllowMultipleHubsInstancesEnabled, toggleAllowMultipleHubsInstancesEnabled] =
    useTogglePreference("allowMultipleHubsInstances");
  const [isDisableIdleDetectionEnabled, toggleDisableIdleDetectionEnabled] =
    useTogglePreference("disableIdleDetection");
  const [isLazyLoadSceneMediaEnabled, toggleLazyLoadSceneMediaEnabled] = useTogglePreference("lazyLoadSceneMedia");
  const [isPreferMobileObjectInfoPanelEnabled, togglePreferMobileObjectInfoPanelEnabled] =
    useTogglePreference("preferMobileObjectInfoPanel");

  const preferenceSelectHandlers = {
    audioPanningQuality: value => store.update({ preferences: { audioPanningQuality: value } }),
    materialQualitySetting: value =>
      store.update({ preferences: { materialQualitySetting: value, shouldPromptForRefresh: true } }),
    aaMode: value => store.update({ preferences: { aaMode: value, shouldPromptForRefresh: true } }),
    locale: value => store.update({ preferences: { locale: value } }),
    theme: value => store.update({ preferences: { theme: value } }),
    nametagVisibility: value => store.update({ preferences: { nametagVisibility: value } })
  };

  const useSelectPreference = preferenceKey => {
    const preferenceValue = store.state.preferences[preferenceKey];
    const onChange = useCallback(
      (_, data) => {
        preferenceSelectHandlers[preferenceKey](data.value);
      },
      [preferenceKey]
    );
    return [preferenceValue, onChange];
  };

  const [audioPanningQuality, onAudioPanningQualityChange] = useSelectPreference("audioPanningQuality");
  const [materialQualitySetting, onMaterialQualitySettingChange] = useSelectPreference("materialQualitySetting");
  const [aaMode, onAAModeChange] = useSelectPreference("aaMode");
  const [locale, onLocaleChange] = useSelectPreference("locale");
  const [theme, onThemeChange] = useSelectPreference("theme");
  const [nametagVisibility, onNametagVisibilityChange] = useSelectPreference("nametagVisibility");

  const prefSchema = useMemo(() => store.schema.definitions.preferences.properties, [store]);

  const onResetToDefaultAudio = useCallback(() => {
    micDeviceChanged(prefSchema["preferredMic"].default);
    speakerDeviceChanged(prefSchema["preferredSpeakers"].default);

    store.update({
      preferences: {
        audioPanningQuality: prefSchema["audioPanningQuality"].default,
        globalVoiceVolume: prefSchema["globalVoiceVolume"].default,
        globalMediaVolume: prefSchema["globalMediaVolume"].default,
        globalSFXVolume: prefSchema["globalSFXVolume"].default,
        audioClippingThreshold: prefSchema["audioClippingThreshold"].default,
        disableSoundEffects: prefSchema["disableSoundEffects"].default,
        disableEchoCancellation: prefSchema["disableEchoCancellation"].default || false,
        disableNoiseSuppression: prefSchema["disableNoiseSuppression"].default || false,
        disableAutoGainControl: prefSchema["disableAutoGainControl"].default || false,
        enableAudioClipping: prefSchema["enableAudioClipping"].default,
        showAudioDebugPanel: prefSchema["showAudioDebugPanel"].default,
        disableLeftRightPanning: prefSchema["disableLeftRightPanning"].default
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefSchema, micDeviceChanged, speakerDeviceChanged]);

  const onResetToDefaultVideo = useCallback(() => {
    videoDeviceChanged(prefSchema["preferredCamera"].default);
  }, [prefSchema, videoDeviceChanged]);

  const onResetToDefaultGraphics = useCallback(() => {
    store.update({
      preferences: {
        maxResolutionWidth: undefined,
        maxResolutionHeight: undefined,
        materialQualitySetting: prefSchema["materialQualitySetting"].default,
        enableDynamicShadows: prefSchema["enableDynamicShadows"].default,
        disableAutoPixelRatio: prefSchema["disableAutoPixelRatio"].default,
        enablePostEffects: prefSchema["enablePostEffects"].default,
        enableBloom: prefSchema["enableBloom"].default,
        aaMode: prefSchema["aaMode"].default
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefSchema]);

  const onResetToDefaultMovement = useCallback(() => {
    store.update({
      preferences: {
        snapRotationDegrees: prefSchema["snapRotationDegrees"].default,
        movementSpeedModifier: prefSchema["movementSpeedModifier"].default,
        disableMovement: prefSchema["disableMovement"].default,
        disableBackwardsMovement: prefSchema["disableBackwardsMovement"].default,
        disableStrafing: prefSchema["disableStrafing"].default,
        disableTeleporter: prefSchema["disableTeleporter"].default
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefSchema]);

  const onResetToDefaultTouchscreen = useCallback(() => {
    store.update({
      preferences: {
        enableOnScreenJoystickLeft: prefSchema["enableOnScreenJoystickLeft"].default,
        enableOnScreenJoystickRight: prefSchema["enableOnScreenJoystickRight"].default,
        enableGyro: prefSchema["enableGyro"].default,
        invertTouchscreenCameraMove: prefSchema["invertTouchscreenCameraMove"].default
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefSchema]);

  const onResetToDefaultGeneral = useCallback(() => {
    store.update({
      preferences: {
        locale: prefSchema["locale"].default,
        theme: prefSchema["theme"].default,
        nametagVisibility: prefSchema["nametagVisibility"].default,
        nametagVisibilityDistance: prefSchema["nametagVisibilityDistance"].default,
        enableOnScreenJoystickLeft: prefSchema["enableOnScreenJoystickLeft"].default,
        enableOnScreenJoystickRight: prefSchema["enableOnScreenJoystickRight"].default,
        enableGyro: prefSchema["enableGyro"].default,
        invertTouchscreenCameraMove: prefSchema["invertTouchscreenCameraMove"].default,
        animateWaypointTransitions: prefSchema["animateWaypointTransitions"].default,
        showFPSCounter: prefSchema["showFPSCounter"].default,
        allowMultipleHubsInstances: prefSchema["allowMultipleHubsInstances"].default,
        disableIdleDetection: prefSchema["disableIdleDetection"].default,
        lazyLoadSceneMedia: prefSchema["lazyLoadSceneMedia"].default,
        preferMobileObjectInfoPanel: prefSchema["preferMobileObjectInfoPanel"].default,
        showRtcDebugPanel: prefSchema["showRtcDebugPanel"].default,
        cursorSize: prefSchema["cursorSize"].default
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefSchema]);

  const intl = useIntl();

  const promptForRefresh = intl.formatMessage({
    id: "preferences-screen.prompt-for-refresh",
    defaultMessage:
      "Your preferences are saved, but some of your changes will not take effect until you refresh the page."
  });

  useEffect(() => {
    if (store.state.preferences.shouldPromptForRefresh) {
      notificationsCenter.info(promptForRefresh, null, { timeout: 5000 });
    }
  }, [promptForRefresh, store.state.preferences.shouldPromptForRefresh]);

  const [settings, setSettings] = useState({ ...hub });

  // Update settings when hub changes
  useEffect(() => {
    setSettings({ ...hub });
  }, [hub]);

  const onSaveVerseSettings = () => {
    if (selectedTab === "verse") {
      try {
        hubChannel.updateHub(settings);
        dashboardChannel.updateHub(settings);

        NotifiablePermissions.forEach(perm => {
          if (APP.hub.member_permissions[perm] !== settings.member_permissions[perm]) {
            hubChannel.sendMessage(
              {
                permission: perm,
                status: settings.member_permissions[perm]
              },
              "permission"
            );
          }
        });

        notificationsCenter.success(
          intl.formatMessage({
            id: "room-settings-sidebar.verse-settings-saved",
            defaultMessage: "Settings saved successfully"
          })
        );

        // setSettingsHasChanges(false);
      } catch (e) {
        console.error(e);

        notificationsCenter.error(
          intl.formatMessage({
            id: "room-settings-sidebar.verse-settings-error",
            defaultMessage: "Error saving settings"
          })
        );
      }
    }
  };

  const renderContent = () => {
    switch (selectedTab) {
      case "verse": {
        return showVerseSettings ? (
          <VerseSettingsContainer
            styles={styles}
            scene={scene}
            store={store}
            hubChannel={hubChannel}
            settings={settings}
            setSettings={setSettings}
            verse={hub}
          />
        ) : (
          <></>
        );
      }
      case "general":
        return (
          <GeneralSettings onResetToDefaultGeneral={onResetToDefaultGeneral}>
            <>
              <div className={styles.row}>
                <LocaleSelect locale={locale} onLocaleChange={onLocaleChange} />
              </div>
              <div className={styles.row}>
                <ThemeSelect theme={theme} onThemeChange={onThemeChange} />
              </div>
              <div className={styles.row}>
                <NametagVisibilitySelect
                  nametagVisibility={nametagVisibility}
                  onNametagVisibilityChange={onNametagVisibilityChange}
                />
              </div>
              <div className={styles.row}>
                <NametagVisibilityDistanceSlider
                  nametagVisibilityDistance={nametagVisibilityDistance}
                  onNametagVisibilityDistanceChange={onNametagVisibilityDistanceChange}
                />
              </div>
              <div className={styles.row}>
                <AnimateWaypointTransitionsSwitch
                  animateWaypointTransitions={isAnimateWaypointTransitionsEnabled}
                  onAnimateWaypointTransitionsChange={toggleAnimateWaypointTransitionsEnabled}
                />
              </div>
              <div className={styles.row}>
                <ShowFPSCounterSwitch
                  showFPSCounter={isShowFPSCounterEnabled}
                  onShowFPSCounterChange={toggleShowFPSCounterEnabled}
                />
              </div>
              <div className={styles.row}>
                <AllowMultipleHubsInstancesSwitch
                  allowMultipleHubsInstances={isAllowMultipleHubsInstancesEnabled}
                  onAllowMultipleHubsInstancesChange={toggleAllowMultipleHubsInstancesEnabled}
                />
              </div>
              <div className={styles.row}>
                <DisableIdleDetectionSwitch
                  disableIdleDetection={isDisableIdleDetectionEnabled}
                  onDisableIdleDetectionChange={toggleDisableIdleDetectionEnabled}
                />
              </div>
              <div className={styles.row}>
                <LazyLoadSceneMediaSwitch
                  lazyLoadSceneMedia={isLazyLoadSceneMediaEnabled}
                  onLazyLoadSceneMediaChange={toggleLazyLoadSceneMediaEnabled}
                />
              </div>
              <div className={styles.row}>
                <PreferMobileObjectInfoPanelSwitch
                  preferMobileObjectInfoPanel={isPreferMobileObjectInfoPanelEnabled}
                  onPreferMobileObjectInfoPanelChange={togglePreferMobileObjectInfoPanelEnabled}
                />
              </div>
              {/* <div className={styles.row}>
                <ShowRTCDebugPanelSwitch
                  showRTCDebugPanel={isShowRTCDebugPanelEnabled}
                  onShowRTCDebugPanelChange={toggleShowRTCDebugPanelEnabled}
                />
              </div> */}
              <div className={styles.row}>
                <CursorSizeSlider cursorSize={cursorSize} onCursorSizeChange={onCursorSizeChange} />
              </div>
            </>
          </GeneralSettings>
        );

      case "audiovideo":
        return (
          <AudioVideoSettings
            onResetToDefaultAudio={onResetToDefaultAudio}
            onResetToDefaultVideo={onResetToDefaultVideo}
            audioChildren={
              <>
                <PreferredMicSelect
                  microphoneOptions={micDevices}
                  onMicrophoneChange={micDeviceChanged}
                  isMicEnabled={isMicEnabled}
                />
                <PreferredSpeakerSelect
                  speakerDevices={speakerDevices}
                  onSpeakerChange={speakerDeviceChanged}
                  isSpeakerEnabled={true}
                />
                <div className={styles.row}>
                  <VoiceVolumeSlider voiceVolume={voiceVolume} onVoiceVolumeChange={onVoiceVolumeChange} />
                </div>
                <div className={styles.row}>
                  <MediaVolumeSlider mediaVolume={mediaVolume} onMediaVolumeChange={onMediaVolumeChange} />
                </div>
                <div className={styles.row}>
                  <SpecialEffectsVolumeSlider
                    specialEffectsVolume={specialEffectsVolume}
                    onSpecialEffectsVolumeChange={onSpecialEffectsVolumeChange}
                  />
                </div>
                <div className={styles.row}>
                  <PanningQualitySelect
                    panningQuality={audioPanningQuality}
                    onPanningQualityChange={onAudioPanningQualityChange}
                  />
                </div>
                <div className={styles.row}>
                  <DisableSoundEffectsSwitch
                    disableSoundEffects={isSoundEffectsDisabled}
                    onDisableSoundEffectsChange={toggleSoundEffectsDisabled}
                  />
                </div>
                <div className={styles.row}>
                  <DisableMicEchoCancellationSwitch
                    disableMicEchoCancellation={isMicEchoCancellationDisabled}
                    onDisableMicEchoCancellationChange={toggleMicEchoCancellationDisabled}
                  />
                </div>
                <div className={styles.row}>
                  <DisableMicNoiseSuppressionSwitch
                    disableMicNoiseSuppression={isMicNoiseSuppressionDisabled}
                    onDisableMicNoiseSuppressionChange={toggleMicNoiseSuppressionDisabled}
                  />
                </div>
                <div className={styles.row}>
                  <DisableMicAutoGainControlSwitch
                    disableMicAutoGainControl={isMicAutoGainControlDisabled}
                    onDisableMicAutoGainControlChange={toggleMicAutoGainControlDisabled}
                  />
                </div>
                <div className={styles.row}>
                  <EnableAudioClippingSwitch
                    enableAudioClipping={isAudioClippingEnabled}
                    onEnableAudioClippingChange={toggleAudioClippingEnabled}
                  />
                </div>
                <div className={styles.row}>
                  <AudioClippingThresholdSlider
                    audioClippingThreshold={audioClippingThreshold}
                    onAudioClippingThresholdChange={onAudioClippingThresholdChange}
                  />
                </div>
                <div className={styles.row}>
                  <ShowAudioDebugPanelSwitch
                    showAudioDebugPanel={isAudioDebugPanelShown}
                    onShowAudioDebugPanelChange={toggleAudioDebugPanelShown}
                  />
                </div>
                <div className={styles.row}>
                  <DisableAudioPanningSwitch
                    disableAudioPanning={isAudioPanningDisabled}
                    onDisableAudioPanningChange={toggleAudioPanningDisabled}
                  />
                </div>
              </>
            }
            videoChildren={
              <>
                <PreferredVideoSelect
                  videoOptions={videoDevices}
                  onVideoChange={videoDeviceChanged}
                  isVideoEnabled={true}
                  fetchVideoDevices={handleFetchVideoDevices}
                />
              </>
            }
          />
        );

      case "graphics":
        return (
          <GraphicsSettings onResetToDefaultGraphics={onResetToDefaultGraphics}>
            <div className={styles.row}>
              <MaxResolutionSlider store={store} />
            </div>
            <div className={styles.row}>
              <MaterialQualitySelect
                materialQualitySetting={materialQualitySetting}
                onMaterialQualityChange={onMaterialQualitySettingChange}
              />
            </div>
            <div className={styles.row}>
              <EnableDynamicShadowsSwitch
                enableDynamicShadows={isDynamicShadowsEnabled}
                onEnableDynamicShadowsChange={toggleDynamicShadowsEnabled}
              />
            </div>
            <div className={styles.row}>
              <DisableAutoPixelRatioSwitch
                disableAutoPixelRatio={isAutoPixelRatioDisabled}
                onDisableAutoPixelRatioChange={toggleAutoPixelRatioDisabled}
              />
            </div>
            {/* <div className={styles.row}>
              <EnablePostEffectsSwitch
                enablePostEffects={isPostEffectsEnabled}
                onEnablePostEffectsChange={togglePostEffects}
              />
            </div>
            <div className={styles.row}>
              <EnableBloomSwitch
                disabled={!isPostEffectsEnabled}
                enableBloom={isPostEffectsEnabled && isBloomEnabled}
                onEnableBloomChange={toggleBloomEnabled}
              />
            </div>
            <div className={styles.row}>
              <AntiAliasingModeSelect
                disabled={!isPostEffectsEnabled}
                aaMode={aaMode}
                onAAModeChange={onAAModeChange}
              />
            </div> */}
          </GraphicsSettings>
        );

      case "controls":
        return (
          <ControlsSettings
            movementChildren={
              <>
                <div className={styles.row}>
                  <SnapRotationDegreesSlider
                    snapRotationDegrees={snapRotationDegrees}
                    onSnapRotationDegreesChange={onSnapRotationDegreesChange}
                  />
                </div>
                <div className={styles.row}>
                  <DisableMovementSwitch
                    disableMovement={isMovementDisabled}
                    onDisableMovementChange={toggleMovementDisabled}
                  />
                </div>
                <div className={styles.row}>
                  <DisableBackwardsMovementSwitch
                    disableBackwardsMovement={isBackwardsMovementDisabled}
                    onDisableBackwardsMovementChange={toggleBackwardsMovementDisabled}
                  />
                </div>
                <div className={styles.row}>
                  <DisableStrafingSwitch
                    disableStrafing={isStrafingDisabled}
                    onDisableStrafingChange={toggleStrafingDisabled}
                  />
                </div>
                <div className={styles.row}>
                  <DisableTeleporterSwitch
                    disableTeleporter={isTeleporterDisabled}
                    onDisableTeleporterChange={toggleTeleporterDisabled}
                  />
                </div>
                <div className={styles.row}>
                  <MovementSpeedSlider
                    movementSpeedModifier={movementSpeedModifier}
                    onMovementSpeedModifierChange={onMovementSpeedModifierChange}
                  />
                </div>
              </>
            }
            touchscreenChildren={
              <>
                <div className={styles.row}>
                  <EnableOnScreenJoystickLeftSwitch
                    enableOnScreenJoystickLeft={isOnScreenJoystickLeftEnabled}
                    onEnableOnScreenJoystickLeftChange={toggleOnScreenJoystickLeftEnabled}
                  />
                </div>
                <div className={styles.row}>
                  <EnableOnScreenJoystickRightSwitch
                    enableOnScreenJoystickRight={isOnScreenJoystickRightEnabled}
                    onEnableOnScreenJoystickRightChange={toggleOnScreenJoystickRightEnabled}
                  />
                </div>
                <div className={styles.row}>
                  <EnableGyroSwitch enableGyro={isGyroEnabled} onEnableGyroChange={toggleGyroEnabled} />
                </div>
                <div className={styles.row}>
                  <InvertTouchscreenCameraMoveSwitch
                    invertTouchscreenCameraMove={isTouchscreenCameraMoveInverted}
                    onInvertTouchscreenCameraMoveChange={toggleTouchscreenCameraMoveInverted}
                  />
                </div>
              </>
            }
            onResetToDefaultMovement={onResetToDefaultMovement}
            onResetToDefaultTouchscreen={onResetToDefaultTouchscreen}
          />
        );

      default:
        return null;
    }
  };

  return (
    <RoomSettingsSidebar
      selectedTab={selectedTab}
      onTabSelect={handleTabSelect}
      open={open}
      onOpenChange={onOpenChange}
      onSave={onSaveVerseSettings}
      settingsHasChanges={true}
    >
      {renderContent()}
    </RoomSettingsSidebar>
  );
}

RoomSettingsSidebarContainer.propTypes = {
  scene: PropTypes.object.isRequired,
  store: PropTypes.object.isRequired,
  hubChannel: PropTypes.object.isRequired,
  hub: PropTypes.object.isRequired,
  dashboardChannel: PropTypes.object.isRequired
};

export default RoomSettingsSidebarContainer;
